import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  "Professional organization",
  "Personal concierge services",
  "A project I've been putting off",
  "Something else entirely",
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  topic: "",
  message: "",
  preferred_contact: "email",
  company: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        topic: form.topic || "General inquiry",
      });
      setSubmitted(true);
      toast.success("Thank you — your note is on its way to Kristina.");
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact | Porter New Orleans — Professional Organizer & Personal Concierge"
        description="Get in touch with Porter New Orleans — professional organization and personal concierge services for Greater New Orleans. Send a note and Kristina will reply personally."
        path="/contact"
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 pt-16 sm:pt-24 lg:pb-32" data-testid="contact-page">
        <Reveal y={16}>
          <p className="flex items-center justify-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-gold-dark">
            <span className="h-px w-8 bg-gold" aria-hidden="true" /> Contact <span className="h-px w-8 bg-gold" aria-hidden="true" />
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mx-auto mt-7 max-w-4xl text-center font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-navy">
            How can we <em className="text-gold-dark">help?</em>
          </h1>
          <p className="mx-auto mt-8 max-w-xl text-center text-base sm:text-lg leading-relaxed text-navy/70">
            Tell us a little about what you need. There&rsquo;s no obligation — just a friendly,
            honest conversation about how Porter can make your days lighter. Every note is read
            and answered personally.
          </p>
        </Reveal>

        <div className="mx-auto mt-16 max-w-3xl">
          <Reveal delay={0.12}>
            {submitted ? (
              <div className="flex flex-col items-start justify-center rounded-3xl border border-gold/40 bg-white p-10 sm:p-14" data-testid="contact-success-message">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-navy text-gold-light">
                  <CheckCircle2 size={26} />
                </span>
                <h2 className="mt-8 font-serif text-4xl leading-snug text-navy">
                  Thank you. <em className="text-gold-dark">Consider it handled.</em>
                </h2>
                <p className="mt-5 max-w-md leading-relaxed text-navy/70">
                  Your note has been received, and Kristina will be in touch with you personally —
                  usually within one business day.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="rounded-3xl border border-navy/10 bg-white p-8 sm:p-12"
                data-testid="contact-form"
              >
                <div className="grid gap-7 sm:grid-cols-2">
                  <div className="space-y-2.5">
                    <Label htmlFor="cf-name" className="text-xs font-bold uppercase tracking-[0.18em] text-navy/60">Name *</Label>
                    <Input
                      id="cf-name"
                      data-testid="contact-name-input"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className="h-12 rounded-xl border-navy/15 bg-cream/50 focus-visible:border-navy focus-visible:ring-0"
                    />
                  </div>
                  <div className="space-y-2.5">
                    <Label htmlFor="cf-email" className="text-xs font-bold uppercase tracking-[0.18em] text-navy/60">Email *</Label>
                    <Input
                      id="cf-email"
                      type="email"
                      data-testid="contact-email-input"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      className="h-12 rounded-xl border-navy/15 bg-cream/50 focus-visible:border-navy focus-visible:ring-0"
                    />
                  </div>
                  <div className="space-y-2.5">
                    <Label htmlFor="cf-phone" className="text-xs font-bold uppercase tracking-[0.18em] text-navy/60">Phone</Label>
                    <Input
                      id="cf-phone"
                      type="tel"
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="Your phone number"
                      className="h-12 rounded-xl border-navy/15 bg-cream/50 focus-visible:border-navy focus-visible:ring-0"
                    />
                  </div>
                  <div className="space-y-2.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.18em] text-navy/60">How can Porter help?</Label>
                    <Select value={form.topic} onValueChange={(v) => setForm((f) => ({ ...f, topic: v }))}>
                      <SelectTrigger data-testid="contact-topic-select" className="h-12 rounded-xl border-navy/15 bg-cream/50 focus:border-navy focus:ring-0">
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent>
                        {TOPICS.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`topic-${t.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "")}`}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-7 space-y-2.5">
                  <Label htmlFor="cf-message" className="text-xs font-bold uppercase tracking-[0.18em] text-navy/60">Message *</Label>
                  <Textarea
                    id="cf-message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us a little about what you need — a cluttered closet, a playroom that's taken over, a to-do list that's out of hand…"
                    className="rounded-xl border-navy/15 bg-cream/50 focus-visible:border-navy focus-visible:ring-0"
                  />
                </div>

                <div className="mt-7">
                  <Label className="text-xs font-bold uppercase tracking-[0.18em] text-navy/60">How should Kristina reply?</Label>
                  <RadioGroup
                    value={form.preferred_contact}
                    onValueChange={(v) => setForm((f) => ({ ...f, preferred_contact: v }))}
                    className="mt-3 flex flex-wrap gap-3"
                    data-testid="contact-preferred-radio"
                  >
                    {[
                      { value: "email", label: "Email me" },
                      { value: "phone", label: "Call me" },
                      { value: "text", label: "Text me" },
                    ].map((opt) => (
                      <Label
                        key={opt.value}
                        htmlFor={`pc-${opt.value}`}
                        data-testid={`preferred-${opt.value}`}
                        className={`flex cursor-pointer items-center gap-2.5 rounded-full border px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                          form.preferred_contact === opt.value
                            ? "border-navy bg-navy text-cream"
                            : "border-navy/20 text-navy/70 hover:border-navy/50"
                        }`}
                      >
                        <RadioGroupItem value={opt.value} id={`pc-${opt.value}`} className="sr-only" />
                        {opt.label}
                      </Label>
                    ))}
                  </RadioGroup>
                </div>

                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={update("company")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="group mt-9 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-bold tracking-wide text-cream transition-all duration-300 hover:bg-navy-light disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send your note"}
                  {!submitting && <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />}
                </button>
                <p className="mt-4 text-center text-xs leading-relaxed text-navy/50">
                  Your note goes straight to Kristina&rsquo;s inbox — never shared, never sold.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
